import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { getArticlesBySection } from "@/data/articles";
import { articleImageAlts } from "@/data/articleImages";
import { inferNavdihTags, NAVDIH_TAG_ORDER, type NavdihTag } from "@/lib/articleTags";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-navdih.webp";

export const Route = createFileRoute("/navdih")({
  head: () => {
    const url = "https://kupish.si/navdih";
    const title = "Navdih & zgodbe — kupish.si";
    const description =
      "Nasveti za nakup, ideje za darila in vodniki za prodajalce v spletnem nakupovalnem središču kupish.si.";
    const firstArticle = [
      ...getArticlesBySection("ideje-in-navdih"),
      ...getArticlesBySection("unikatne-vsebine"),
    ].sort((a, b) => (a.date < b.date ? 1 : -1))[0];
    const firstImage = firstArticle?.featuredImage
      ? (firstArticle.featuredImage.startsWith("http") ? firstArticle.featuredImage : firstArticle.featuredImage)
      : null;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        ...(firstImage ? [{ rel: "preload", as: "image", href: firstImage, fetchpriority: "high" }] : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            inLanguage: "sl-SI",
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
              { "@type": "ListItem", position: 2, name: "Navdih & zgodbe", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const all = useMemo(() => {
    const merged = [
      ...getArticlesBySection("ideje-in-navdih"),
      ...getArticlesBySection("unikatne-vsebine"),
    ];
    // odstrani navodila/tehnične vsebine, ki spadajo v predstavitev ponudnika
    const excluded = new Set([
      "registracija-najem-spletne-trgovine",
      "prijava-urejanje-trgovine",
      "kaj-vse-lahko-urejam-v-nadzorni-plosci",
      "kaj-vse-lahko-spremljam-v-nadzorni-plosci",
      "dodajanje-izdelkov",
    ]);
    return merged
      .filter((a) => !excluded.has(a.slug))
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .map((a) => ({ ...a, tags: inferNavdihTags(a) }));
  }, []);

  const counts = useMemo(() => {
    const c = new Map<NavdihTag, number>();
    for (const a of all) for (const t of a.tags) c.set(t, (c.get(t) ?? 0) + 1);
    return c;
  }, [all]);

  const availableTags = NAVDIH_TAG_ORDER.filter((t) => (counts.get(t) ?? 0) > 0);
  const [active, setActive] = useState<NavdihTag | null>(null);
  const visible = active ? all.filter((a) => a.tags.includes(active)) : all;

  return (
    <section className="pb-24">
      <PageHero src={pageCover} alt="Navdih za nakup na kupish.si — minimalističen 3D prikaz ideje">
        <div className="eyebrow">Za obiskovalce</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-3 leading-tight">
          Navdih & <span className=" text-accent">zgodbe</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
          Nasveti za nakup, ideje za darila in vodniki za prodajalce — vse
          na enem mestu.
        </p>
      </PageHero>
      <div className="mx-auto max-w-5xl px-6 pt-10">

      {/* Značke */}
      <div className="mt-10 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActive(null)}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition-colors ${
            active === null
              ? "bg-accent text-accent-foreground border-accent"
              : "border-border hover:border-accent hover:text-accent"
          }`}
        >
          Vse objave ({all.length})
        </button>
        {availableTags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActive(t === active ? null : t)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full border transition-colors ${
              active === t
                ? "bg-accent text-accent-foreground border-accent"
                : "border-border hover:border-accent hover:text-accent"
            }`}
          >
            {t} ({counts.get(t)})
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-8">
        {visible.map((a, i) => (
          <Link
            key={a.slug}
            to="/clanek/$slug"
            params={{ slug: a.slug }}
            className="group block border border-border rounded-lg overflow-hidden hover:border-accent transition-colors bg-card"
          >
            {a.featuredImage && (
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={a.featuredImage}
                  alt={articleImageAlts[a.slug] ?? a.title}
                  title={a.title}
                  width={640}
                  height={400}
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : undefined}
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              </div>
            )}
            <div className="p-6">
              <h2 className="font-serif text-2xl leading-snug group-hover:text-accent text-foreground min-h-[3.4rem]">
                {a.title}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3 min-h-[3.75rem]">{a.excerpt}</p>
              <div className="mt-3 flex flex-wrap gap-1.5 min-h-[1.5rem]">
                {a.tags.slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-border text-muted-foreground"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <span className="mt-4 inline-block text-xs uppercase tracking-wider text-accent">
                Preberi →
              </span>
            </div>
          </Link>
        ))}
      </div>
      </div>
    </section>
  );
}
